import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ApiResponse } from './auth.service';
import { ClientCrypto } from '../utils/client-crypto';
import { environment } from '../../environments/environment';

/** Esito restituito dalla stored function `07_frontend.updatePassword`. */
export type PasswordResetResult = 'Done' | 'Not Done';

/**
 * Reset password tramite il token ricevuto per email. Nessuna dipendenza da uno stato autenticato:
 * la pagina che usa questo servizio e' pubblica e il token e' l'unica credenziale.
 */
@Injectable({
  providedIn: 'root'
})
export class PasswordResetService {
  constructor(
    private http: HttpClient,
    private clientCrypto: ClientCrypto
  ) {}

  /** Backend: POST /password-reset?token=... { password } (no auth). */
  resetPassword(token: string, newPassword: string): Observable<PasswordResetResult> {
    const normalizedToken = (token || '').trim();
    if (!normalizedToken) {
      return throwError(() => new Error('passwordReset.messages.missingToken'));
    }

    const baseUrl = (environment.apiUrl || '').replace(/\/+$/u, '');
    const endpoint = `${baseUrl}/password-reset?token=${encodeURIComponent(normalizedToken)}`;
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

    return this.http.post<ApiResponse<{ Result?: PasswordResetResult }>>(
      endpoint,
      { password: newPassword },
      { headers }
    ).pipe(
      map(response => {
        if (response?.error?.message) {
          throw new Error(response.error.message);
        }
        return response?.payload?.Result === 'Done' ? 'Done' : 'Not Done';
      }),
      catchError(error => {
        const message =
          error?.error?.error?.message ||
          error?.error?.message ||
          error?.message ||
          'passwordReset.messages.genericError';
        return throwError(() => new Error(message));
      })
    );
  }

  /** Cifra la password come attende il backend (RSA + base64). */
  encryptPassword(password: string): Promise<string> {
    return this.clientCrypto.encryptRSA(password);
  }
}
